import React, { Component } from "react"
import Modal from "react-bootstrap/lib/Modal"
import ColorPicker from "../color_picker"
import {
    PRICE_USER_PER_MONTH,
    PRICE_USER_PER_YEAR,
} from "../../common/constants/app_consts"
import { appStrings } from "../../common/strings/app_strings"
import {
    generateDefaultInviteFields,
    parseTextAreaToFields,
    removeEmptyFields,
    validateFields,
} from "../../util/onboarding_utils"
import { InviteTeamMembers } from "../invite_team_members"
import { makeToast } from "../../util/toast_util"
import { alerts } from "../../util/alert_error_utils"
import { ExpiredTrialAlertWindow } from "../alert_window"

const {
    freeTrialHeader,
    freeTrialExplanation,
    pricingHeader,
    perUserYear,
    perUserMonth,
    trialUpgradeExplanation,
    warning,
    nameAlreadyExists,
} = appStrings.teamEditModal

// show trial info if creating this team will start a trial
// not always free, not edit mode, paymentStatus is team, and there is no running trial
const shouldShowTrial = (editMode, alwaysFree, trialEnd, paymentStatus) => {
    const showTrialInfo =
        !editMode && !alwaysFree && trialEnd === null && paymentStatus === ""
    return showTrialInfo
}

class TeamEditModal extends Component {
    constructor(props) {
        super(props)

        this.handleClose = this.handleClose.bind(this)
        this.handleSave = this.handleSave.bind(this)
        this.handleChange = this.handleChange.bind(this)
        this.clearModal = this.clearModal.bind(this)
        this.selectOnce = this.selectOnce.bind(this)

        const { editTeamId, teams } = props
        const selectedTeam = teams.find(t => t.id === editTeamId)

        this.state = {
            teamId: editTeamId,
            name: selectedTeam ? selectedTeam.name : undefined,
            color: selectedTeam ? selectedTeam.color : undefined,
            editMode: !!editTeamId,
            dirty: false,
            fields: generateDefaultInviteFields(3),
            isTextArea: false,
            textAreaContent: "",
            showInviteTeam: false,
            showAlertModal: false,
        }
    }

    clearModal() {
        this.setState({
            teamId: null,
            name: null,
            color: null,
            editMode: false,
            dirty: false,
        })
    }

    updateState = (key, value, fn) => {
        this.setState({ [key]: value }, () => fn)
    }

    setTitle(team) {
        const prefix = team ? "Edit" : "Create New"
        document.title = `${prefix} Team | Shoutbase`
    }

    isNotUnique(teams, name) {
        const existingTeam = teams.find(t => t.name === name)
        return !!existingTeam
    }

    hideAlertModal = () => {
        this.setState({ showAlertModal: false })
    }

    openUpgradeModal = () => {
        this.setState({ showAlertModal: false }, () => {
            this.props.setPaymentModalOpen(true)
        })
    }

    handleSave() {
        const { createTeam, profile, putTeam, teams, trialEnd } = this.props
        const { name, color, editMode, teamId, showInviteTeam } = this.state
        const accountIsExpired = trialEnd && new Date(trialEnd) < new Date()
        if (!editMode && accountIsExpired) {
            this.setState({ showAlertModal: true })
            return
        }

        // edit team
        if (editMode) {
            const selectedTeam = teams.find(t => t.id === teamId)
            putTeam({
                ownerId: selectedTeam.ownerId,
                id: teamId,
                color: color,
                name: name,
                shortname: name[0],
                description: "",
            })

            this.clearModal()
            this.handleClose()
            return
        }

        // create a new team
        if (!editMode & !showInviteTeam) {
            if (this.isNotUnique(teams, name)) {
                // needs to toast that name is not unique
                makeToast(nameAlreadyExists, warning)
                return
            }
            createTeam(
                {
                    ownerId: profile.id,
                    color: color,
                    name: name,
                    shortName: name[0],
                    description: "",
                },
                this.props.history,
            )
            // after creating the team, show bulk invites
            this.setState({ showInviteTeam: true })
        }

        // send invites
        if (showInviteTeam) {
            this.submitInvites()
        }
    }

    submitInvites = () => {
        const { fields, isTextArea, textAreaContent } = this.state

        const textFields = isTextArea
            ? parseTextAreaToFields(textAreaContent, false)
            : removeEmptyFields(fields)
        if (validateFields(textFields)) {
            // send invites
            const currentTeamId = this.props.history.location.pathname.split(
                "/teams/",
            )[1]
            this.props.sendBulkInvites(this.state.fields, currentTeamId)
            const msg = "Invites sent!"
            makeToast(msg, "success")

            // close modal
            this.handleClose()
            return
        } else {
            if (isTextArea) {
                this.setState({ fields: textFields })
            }
            const { msg } = alerts.validEmailRequired

            return makeToast(msg, "danger")
        }
    }

    handleClose() {
        if (this.props.closeTeamModal) {
            this.props.closeTeamModal()
            this.clearModal()
        }
    }

    setColor(color) {
        this.setState({ color: color })
    }

    selectOnce(ref) {
        ref.select()
        this.setState({ dirty: true })
    }

    handleChange(e) {
        this.setState({ [e.target.name]: e.target.value })
    }

    canSave = () => {
        const { name, showInviteTeam } = this.state
        return !showInviteTeam ? name && !!name.trim() : true
    }

    handleTextAreaChange = value => {
        this.setState({ textAreaContent: value })
    }

    render() {
        const { editMode, name, color, dirty, showInviteTeam } = this.state
        const { trialEnd, paymentStatus, alwaysFree, accountName, profile } = this.props
        const title = showInviteTeam
            ? "Invite Team Members"
            : editMode
                ? "Edit Team"
                : "New Team"
        // trash can to be implemented
        const showTrashCan = false
        const showTrialInfo = shouldShowTrial(
            editMode,
            alwaysFree,
            trialEnd,
            paymentStatus,
        )

        return (
            <Modal
                show={true}
                onHide={this.handleClose}
                className="team-modal-container modal-v2"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>
                <ExpiredTrialAlertWindow
                    isOpen={this.state.showAlertModal}
                    handleCloseAlert={this.hideAlertModal}
                    team={null}
                    accountName={accountName}
                    type="TEAM"
                    upgrade={this.openUpgradeModal}
                    personalTeamId={""}
                    userId={profile.id}
                />
                {!showInviteTeam ? (
                    <Modal.Body className="team-modal-body">
                        <div className="team-modal-content">
                            <div className="form-group">
                                <label>Team name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="name"
                                    ref={ref =>
                                        ref && !dirty && this.selectOnce(ref)
                                    }
                                    value={name || ""}
                                    onChange={this.handleChange}
                                />
                            </div>
                            <ColorPicker
                                color={color}
                                setColor={this.setColor.bind(this)}
                            />
                        </div>
                        {showTrialInfo && (
                            <div className="team-trial-content">
                                <p className="bold">{freeTrialHeader}</p>
                                <p>{freeTrialExplanation}</p>
                                <p className="bold">{pricingHeader}</p>
                                <p>
                                    ${PRICE_USER_PER_MONTH} {perUserMonth}, or
                                </p>
                                <p>
                                    ${PRICE_USER_PER_YEAR} {perUserYear}
                                </p>
                                <p>{trialUpgradeExplanation}</p>
                            </div>
                        )}
                    </Modal.Body>
                ) : (
                    <Modal.Body className="team-modal-body">
                        <InviteTeamMembers
                            fields={this.state.fields}
                            textAreaContent={this.state.textAreaContent}
                            handleTextAreaChange={this.handleTextAreaChange}
                            isTextArea={this.state.isTextArea}
                            updateState={this.updateState}
                        />
                    </Modal.Body>
                )}

                <Modal.Footer>
                    <div className="row team-modal-footer">
                        <div className="team-modal-controls">
                            <div
                                className="team-modal-trash"
                                onClick={this.handleDelete}
                            >
                                {showTrashCan && (
                                    <i
                                        className="fa fa-trash-o"
                                        aria-hidden="true"
                                    />
                                )}
                            </div>
                            <div className="team-btn-group">
                                <button
                                    className="btn btn-default btn-spacer"
                                    type="button"
                                    onClick={this.handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    disabled={!this.canSave()}
                                    className="btn btn-primary btn-spacer"
                                    type="button"
                                    onClick={this.handleSave}
                                >
                                    {showInviteTeam
                                        ? "Send Invites"
                                        : editMode
                                            ? "Save"
                                            : "Create"
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        )
    }
}

export default TeamEditModal



// WEBPACK FOOTER //
// ./src/components/modals/team_edit_modal.jsx